import React from 'react';
import EventEmitter from 'eventemitter3';

// new firebase https://github.com/firebase/firebase-js-sdk/issues/4368
import { initializeApp } from 'firebase/app';
import { toast } from 'react-toastify';
import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';

import { initServer } from './server';
import history from './history';
import { utils } from './utils';
import * as serviceWorker from './serviceWorker';

const metadata = require('../../metadata.json');

const AppClass = class {
  constructor(opts) {
    // disableUserAccount
    // defaultLanguage
    // disableServer
    // themeType
    this.opts = {
      disableUserAccount: opts.disableUserAccount,
      disableServer: opts.disableServer,
      disablePulseIndicator: opts.disablePulseIndicator,
      themeType: opts.themeType,
      defaultLanguage: opts.defaultLanguage,
      endpoint: opts.disableUserAccount,
      toastPosition: opts.toastPosition,
    };

    this.metadata = metadata;

    this.settings = this.loadDomainSettings();
    // init the server
    if (!opts.disableServer) {
      this.server = initServer(opts, this);
    }
    // attach helpers
    this.utils = utils;

    this.serviceWorker = serviceWorker;
    this.eventEmitter = new EventEmitter();
    this.history = history;
    this.themeType =
      opts.themeType ||
      this.clientSettings.getItem('themeType') ||
      this.themeManager.getSystemTheme();
    this.themeListners = [];
    this.languageListeners = [];
    this.language =
      this.clientSettings.getItem('language') ||
      opts.defaultLanguage ||
      i18n.language;
    this.dir = this.languageManager.getDirection(this.language);
    this.eventsData = {
      eventsMap: new Map(),
      listenersMap: new Map(),
      emittersMap: new Map(),
    };
    // Strip cache-bust param after successful update
    const _url = new URL(window.location.href);
    if (_url.searchParams.has('_v')) {
      _url.searchParams.delete('_v');
      window.history.replaceState({}, '', _url.pathname + _url.search);
    }

    this.hostUrl = utils.getUrlParts();
    this.toastPosition = opts.toastPosition || toast.POSITION.BOTTOM_CENTER;
    this.loaded = false;
    if (!opts.disableUserAccount) {
      this.eventsManager.on('event-connected', this, () => {
        this.initUser();
      });
      this.eventsManager.on('event-user-updated', this, () => {
        this.fetchUser();
      });
      // event-resume-login should be private event
      // use event-user-loaded
      this.eventsManager.on('event-resume-login', this, () => {
        this.fetchUser();
      });
    }
    const registerSystemThemeChange = () => {
      const matchMedia = window.matchMedia('(prefers-color-scheme: dark)');

      const registerThemeChange = (e) => {
        const newColorScheme = e.matches ? 'dark' : 'light';
        this.themeManager.changeTheme(newColorScheme);
      };
      if (matchMedia.addEventListener) {
        matchMedia.addEventListener('change', (e) => {
          registerThemeChange(e);
        });
      } else if (matchMedia.addListener) {
        matchMedia.addListener((e) => {
          registerThemeChange(e);
        });
      }
    };
    registerSystemThemeChange();

    // Setup firebase
    const firebaseConfig = {
      apiKey: 'AIzaSyADxsGCMFSzpfDMYPDNMY5fs5dtl5Radyw',
      authDomain: 'siteshub-meronex.firebaseapp.com',
      databaseURL: 'https://siteshub-meronex.firebaseio.com',
      projectId: 'siteshub-meronex',
      storageBucket: 'siteshub-meronex.appspot.com',
      messagingSenderId: '640514951222',
      appId: '1:640514951222:web:e8833118e569f34062f097',
      measurementId: 'G-22Y8FC30JZ',
    };
    const firebaseApp = initializeApp(firebaseConfig);
    this.firebase = firebaseApp;

    // Register service service worker
    serviceWorker.register({
      immediate: true,
      onSuccess: () => {},
      onUpdate: () => {},
    });
  }

  getServerUrl = () => {
    try {
      const httpUrl = this.server._opts.endpoint.replace('ws', 'http');
      const httpUrlArray = httpUrl.split('/');
      return `${httpUrlArray[0]}//${httpUrlArray[2]}`;
    } catch (err) {
      console.error('[App] getServerUrl error:', err);
      throw err;
    }
  };

  setToastPosition = (position) => {
    this.toastPosition = position;
  };
  getToastPosition = () => {
    return this.toastPosition;
  };
  isLocalHost = () => {
    return this.hostUrl.hostname === 'localhost';
  };
  initUser = async () => {
    const user = await this.fetchUser();
    if (!user) {
      if (this.isLogged()) {
        // something is wrong
        // we have user token without user
        console.error('login token without user, logout');
        this.clientSettings.removeItem('token');
        this.clientSettings.removeItem('userId');
        this.clientSettings.removeItem('loginInfo');
        history.push('login');
      }
    } else {
      this.loaded = true;
      this.eventsManager.emit('event-user-loaded', this);
    }
  };
  fetchUser = async (broadcast = true) => {
    const user = await this.server.call('getSessionUser', {});

    this._user = user;
    if (user) {
      this.eventsManager.emit('event-user-loaded', this);
    }

    return user;
  };
  getUser = () => {
    return this._user;
  };
  getSessionCommunity = async () => {
    const result = await this.server.call('getSessionCommunity', {});
    return result;
  };

  isLogged = () => {
    const token = this.clientSettings.getItem('token');
    return token != null;
  };

  logout = () => {
    this.server.logout();
    this.clientSettings.removeItem('token');
    this.clientSettings.removeItem('userId');
    this.clientSettings.removeItem('loginInfo');
    history.push('login');
  };
  loadDomainSettings = async () => {
    const appSettingsNode = document.getElementById('app_settings');
    if (appSettingsNode && appSettingsNode.innerText) {
      this.settings = await JSON.parse(appSettingsNode.innerText);
    } else this.settings = undefined;
  };
  // Function for manipulating client settings stored at the
  // browser local storage
  clientSettings = {
    setItem: (key, item) => {
      const clientSettings = utils.localStorage.getItem('clientSettings') || {};
      clientSettings[key] = item;
      utils.localStorage.setItem('clientSettings', clientSettings);
    },
    getItem: (key) => {
      const clientSettings = utils.localStorage.getItem('clientSettings') || {};
      if (!key) {
        return clientSettings;
      }
      return clientSettings[key];
    },
    removeItem: (key) => {
      const clientSettings = utils.localStorage.getItem('clientSettings') || {};
      delete clientSettings[key];
      return utils.localStorage.setItem('clientSettings', clientSettings);
    },
    getAll: () => {
      return utils.localStorage.getItem('clientSettings');
    },
    removeAll: () => {
      return utils.localStorage.setItem('clientSettings', {});
    },
  };
  userSettings = {
    // get/set general user settings at the server
    set: async (userSettings, cb) => {
      const result = await this.server.call('setUserSettings', {
        userSettings,
      });
      this._user.settings = userSettings;
      if (cb) {
        return cb(userSettings);
      }
      return result;
    },
    get: async (refresh) => {
      let userSettings;
      if (!this._user || refresh) {
        userSettings = await this.server.call('getUserSettings', {});
      } else {
        userSettings = this._user.settings;
      }
      return userSettings;
    },
    // get/set the settings for specific app.
    // if not available then initialize it
    app: {
      get: async (appName) => {
        let settings = {};
        const userSettings = await App.userSettings.get();
        if (userSettings) {
          if (userSettings[appName]) {
            settings = userSettings[appName];
          } else {
            userSettings[appName] = settings;
            await App.userSettings.set(userSettings);
          }
        }

        return settings;
      },
      set: async (appName, settings) => {
        const userSettings = App.utils.clone(await App.userSettings.get());
        if (!userSettings[appName]) {
          userSettings[appName] = {};
        }
        const _mergedSettings = Object.assign(userSettings[appName], settings);
        userSettings[appName] = _mergedSettings;
        await App.userSettings.set(userSettings);
        return settings;
      },
    },
  };
  themeManager = {
    themeType: this.themeType,
    getSystemTheme: () => {
      // Assume light
      let systemTheme = 'light';

      if (window.matchMedia) {
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
          systemTheme = 'dark';
        }
      } else if (window.matchMedia('(prefers-color-scheme: light)').matches) {
        systemTheme = 'light';
      }
      return systemTheme;
    },
    addThemeListener: (listener) => {
      this.themeListners.push(listener);
    },
    changeTheme: (themeType) => {
      this.themeType = themeType;
      this.clientSettings.setItem('themeType', themeType);
      this.themeListners.forEach((listener) => {
        listener(themeType);
      });
    },
  };

  languageManager = {
    rtlLanguages: ['ar'],
    language: this.language,
    isRtl: () => {
      return this.languageManager.rtlLanguages.includes(this.language);
    },
    changeLanguage: (language, cb) => {
      this.language = language;
      this.clientSettings.setItem('language', language);
      i18n.changeLanguage(language, () => {
        if (cb) {
          cb();
        }
      });

      document.body.setAttribute(
        'dir',
        this.languageManager.getDirection(language)
      );

      // this.clientSettings.setItem('themeType', themeType);
      this.languageListeners.forEach((listener) => {
        listener(language);
      });
    },
    addLanguageListener: (listener) => {
      this.languageListeners.push(listener);
    },
    getDirection: (language = this.language) => {
      const dir = this.languageManager.rtlLanguages.includes(language)
        ? 'rtl'
        : 'ltr';
      return dir;
    },
  };
  eventsManager = {
    // Map of "event::listenerId" -> callback for proper cleanup
    _callbackMap: new Map(),
    // the event string, listener object, and callback function
    on: (event, listener, callback) => {
      const listenerId = this.utils.getObjectName(listener);
      const key = `${event}::${listenerId}`;

      // If this listener already registered for this event,
      // remove the old one first to prevent stacking
      if (this.eventsManager._callbackMap.has(key)) {
        const oldCallback = this.eventsManager._callbackMap.get(key);
        this.eventEmitter.removeListener(event, oldCallback);
      }

      this.eventEmitter.on(event, callback);
      this.eventsManager._callbackMap.set(key, callback);

      // Update the events map (for tracing/debugging)
      let eventSet = this.eventsData.eventsMap.get(event);
      if (!eventSet) {
        eventSet = new Set();
      }
      eventSet.add(listenerId);
      this.eventsData.eventsMap.set(event, eventSet);

      // Update the events data maps
      let listenerSet = this.eventsData.listenersMap.get(listenerId);
      if (!listenerSet) {
        listenerSet = new Set();
      }
      listenerSet.add(event);
      this.eventsData.listenersMap.set(listenerId, listenerSet);
    },
    emit: (event, emitter, eventArgs) => {
      const emitterId = this.utils.getObjectName(emitter);
      this.eventEmitter.emit(event, eventArgs);
      const emittersMap = this.eventsData.emittersMap;
      let eventEmittersSet = emittersMap.get(event);
      if (!eventEmittersSet) {
        eventEmittersSet = new Set();
      }
      eventEmittersSet.add(emitterId);
      emittersMap.set(event, eventEmittersSet);
    },
    hasListeners: (event) => {
      return this.eventEmitter.listenerCount(event) > 0;
    },
    listenersCount: (event) => {
      return this.eventEmitter.listenerCount(event);
    },
    removeListener: (event, listener, callback) => {
      const listenerId = this.utils.getObjectName(listener);
      const key = `${event}::${listenerId}`;

      // Use the stored callback if none provided (the common case)
      const cb = callback || this.eventsManager._callbackMap.get(key);
      if (cb) {
        this.eventEmitter.removeListener(event, cb);
      }
      this.eventsManager._callbackMap.delete(key);

      let eventSet = this.eventsData.eventsMap.get(event);
      if (eventSet) {
        eventSet.delete(listenerId);
        this.eventsData.eventsMap.set(event, eventSet);
      }
      this.eventsData.listenersMap.delete(listenerId);
    },
    removeAllListeners: () => {
      this.eventEmitter.removeAllListeners();
      this.eventsManager._callbackMap.clear();
      this.eventsData.eventsMap = new Map();
      this.eventsData.listenersMap = new Map();
    },
    eventEmitter: this.eventEmitter,
    // the current state of the eventsManager
    // for tracing and debugging
    getState: () => {
      return {
        eventsMap: this.eventsData.eventsMap,
        listenersMap: this.eventsData.listenersMap,
        emittersMap: this.eventsData.emittersMap,
        eventNames: this.eventEmitter.eventNames(),
      };
    },
  };

  forceReload = async () => {
    try {
      // Clear all Cache Storage
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) {
      console.warn('[App/forceReload] cache cleanup error:', e);
    }
    // Navigate with cache-bust param to bypass HTTP/CDN cache
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now());
    window.location.replace(url.toString());
  };
};

const onSettingLoad = (App) =>
  new Promise((resolve) => {
    const isPromise = (obj) => obj instanceof Promise;
    const isValidSettings = () => App.settings && !isPromise(App.settings);

    if (isValidSettings()) {
      resolve(App.settings);
    }
    const loadTestInterval = setInterval(async () => {
      await App.loadDomainSettings();
      if (isValidSettings()) {
        clearInterval(loadTestInterval);
        resolve(App.settings);
      }
    }, 25);
  });

const initI18n = (opts, App) => {
  const clientLanguage = App.clientSettings.getItem('language');

  const activeLanguage = clientLanguage || opts.defaultLanguage || 'en';
  App.language = activeLanguage;
  return i18n
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
      resources: opts.translations || {},
      lng: activeLanguage,
      ns: ['prod', 'mock'],
      defaultNS: 'prod',
      keySeparator: '.',
      interpolation: {
        escapeValue: false, // react already safes from xss
        format: function (value, format, lng) {
          if (format === 'number') {
            return Intl.NumberFormat(lng === 'ar' ? 'ar-AE' : 'en', {}).format(
              value
            );
          }
        },
      },
    });
};

export const AppContext = React.createContext(null);
export let App;

export const initApp = async (opts) => {
  // wait for the settings to load
  return new Promise(async (resolve) => {
    App = new AppClass(opts);

    await onSettingLoad(App);
    await initI18n(opts, App);

    //Attach app instance to window
    window.App = App;
    window.app = App;
    resolve(App);
  });
};
